import {
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Flex,
  Image,
  SimpleGrid,
  Button,
  Text,
  Box,
  useBoolean,
  useDisclosure,
  Tag,
} from '@chakra-ui/react';
import { format } from 'date-fns-tz';
import React, { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerFixedButton } from 'components/Drawer';
import FallbackAvatar from 'components/FallbackAvatar';
import LogoLoader from 'components/Loader/LogoLoader';
import { ConfirmationModal } from 'components/Modal';
import ScheduleForm from 'containers/Scheduling/RightDrawer/ScheduleForm';
import { initialScheduleFormData } from 'containers/Scheduling/RightDrawer/ScheduleForm/schema';
import { transformScheduleDataToFormData } from 'containers/Scheduling/shared/FilterGroup/transformScheduleDataToFormData';
import { MONTH_DAY_YEAR_FORMAT } from 'global/appConstants';
import { roles } from 'global/rolesAndPermissions';
import { useDeleteScheduleById } from 'hooks/scheduling/useDeleteScheduleById';
import { useFetchScheduleById } from 'hooks/scheduling/useFetchScheduleById';
import { usePublishSchedule } from 'hooks/scheduling/usePublishSchedule';
import { useUserObject } from 'store/useUserStore';
import { formatTime, formatYearMonthDay } from 'utils/format';
import { isEmptyObjectWithNull } from 'utils/helpers';

import UpdateSeriesConfirmationModal from './UpdateSeriesConfirmationModal';

interface IScheduleDetailsProps {
  onClose: () => void;
  scheduleId: string | null;
}

const ScheduleDetail: React.FC<IScheduleDetailsProps> = ({ onClose, scheduleId }) => {
  const { t } = useTranslation(['scheduling', 'common']);
  const userStore = useUserObject();
  const companyInfo = userStore?.companyInfo || {};

  const [scheduleFormData, setScheduleFormData] = useState<typeof initialScheduleFormData>(initialScheduleFormData);

  const [isScheduleFormOpened, setScheduleFormOpened] = useBoolean();

  const {
    isOpen: isUpdateSeriesEditModalOpen,
    onOpen: onUpdateSeriesEditModalOpen,
    onClose: onUpdateSeriesEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isUpdateSeriesDeleteModalOpen,
    onOpen: onUpdateSeriesDeleteModalOpen,
    onClose: onUpdateSeriesDeleteModalClose,
  } = useDisclosure();
  const {
    isOpen: isUpdateSeriesPublishModalOpen,
    onOpen: onUpdateSeriesPublishModalOpen,
    onClose: onUpdateSeriesPublishModalClose,
  } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { data, isLoading } = useFetchScheduleById(scheduleId);
  const { mutateAsync: mutateAsyncDeleteSchedule, status: deleteScheduleStatus } = useDeleteScheduleById();
  const { mutateAsync: mutateAsyncPublishSchedule, status: publishScheduleStatus } = usePublishSchedule();

  const handleEditButtonClick = () => {
    const formData = transformScheduleDataToFormData(data?.payload);
    setScheduleFormData(formData);
    setScheduleFormOpened.on();
  };

  const handleEditButtonClickForUpdateSeries = () => {
    onUpdateSeriesEditModalOpen();
  };

  const handleAllSeriesEditButtonClick = () => {
    const formData = transformScheduleDataToFormData(data?.payload);
    formData.update_series = true;

    setScheduleFormData(formData);
    setScheduleFormOpened.on();
  };

  const handlePublishButtonClick = () => {
    const postData = {
      schedule_id: data?.payload?.id,
    };

    mutateAsyncPublishSchedule(postData);
  };

  const handlePublishButtonClickforUpdateSeries = () => {
    onUpdateSeriesPublishModalOpen();
  };

  // Delete repeating single schedule
  const publishSingleScheduleInUpdateSeries = () => {
    const publishData = {
      schedule_id: data?.payload?.id,
      repeat: true,
      event_pid: data?.payload?.event_pid,
      update_series: false,
    };

    mutateAsyncPublishSchedule(publishData).then(() => {
      onUpdateSeriesPublishModalClose();
    });
  };

  // Delete repeating all schedule
  const publishAllScheduleInUpdateSeries = () => {
    const publishData = {
      schedule_id: data?.payload?.id,
      repeat: true,
      event_pid: data?.payload?.event_pid,
      update_series: true,
    };

    mutateAsyncPublishSchedule(publishData).then(() => {
      onUpdateSeriesPublishModalClose();
    });
  };

  const handleDeleteButtonClick = () => {
    onDeleteModalOpen();
  };

  const handleDeleteButtonClickforUpdateSeries = () => {
    onUpdateSeriesDeleteModalOpen();
  };

  // Delete non-repeating schedule
  const deleteSchedule = () => {
    const deleteData = {
      id: data?.payload?.id,
      repeat: false,
      event_pid: data?.payload?.event_pid,
      update_series: false,
    };

    mutateAsyncDeleteSchedule(deleteData).then(() => {
      onDeleteModalClose();
      onClose();
    });
  };

  // Delete repeating single schedule
  const deleteSingleScheduleInUpdateSeries = () => {
    const deleteData = {
      id: data?.payload?.id,
      repeat: true,
      event_pid: data?.payload?.event_pid,
      update_series: false,
    };

    mutateAsyncDeleteSchedule(deleteData).then(() => {
      onUpdateSeriesDeleteModalClose();
      onClose();
    });
  };

  // Delete repeating all schedule
  const deleteAllScheduleInUpdateSeries = () => {
    const deleteData = {
      id: data?.payload?.id,
      repeat: true,
      event_pid: data?.payload?.event_pid,
      update_series: true,
    };

    mutateAsyncDeleteSchedule(deleteData).then(() => {
      onUpdateSeriesDeleteModalClose();
      onClose();
    });
  };

  // Format the address
  const formatAddress = (address: any) => {
    if (isEmptyObjectWithNull(address)) {
      return '';
    }

    const addressConcat = `${address?.street ? address?.street + ',' : ''} ${
      address?.city ? address?.city + ',' : ''
    } ${address?.state ? address?.state + ',' : ''} ${address?.country || ''}`;

    return addressConcat.trim();
  };

  if (isScheduleFormOpened) {
    return <ScheduleForm formData={scheduleFormData} onClose={onClose} />;
  }

  return (
    <>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('scheduling:deleteScheduleModalTitle')}
        subTitle={t('scheduling:deleteScheduleModalSubTitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: deleteScheduleStatus === 'loading',
          onClick: deleteSchedule,
        }}
      />

      {/* update series */}
      <UpdateSeriesConfirmationModal
        title={t('scheduling:updateSeriesConfirmationModalTitle')}
        handleNoClick={handleEditButtonClick}
        handleYesClick={handleAllSeriesEditButtonClick}
        isOpen={isUpdateSeriesEditModalOpen}
        onClose={onUpdateSeriesEditModalClose}
      />

      {/* delete series */}
      <UpdateSeriesConfirmationModal
        title={t('scheduling:deleteSeriesConfirmationModalTitle')}
        handleNoClick={deleteSingleScheduleInUpdateSeries}
        handleYesClick={deleteAllScheduleInUpdateSeries}
        isOpen={isUpdateSeriesDeleteModalOpen}
        onClose={onUpdateSeriesDeleteModalClose}
      />

      {/* publish series*/}
      <UpdateSeriesConfirmationModal
        title={t('scheduling:publishSeriesConfirmationModalTitle')}
        handleNoClick={publishSingleScheduleInUpdateSeries}
        handleYesClick={publishAllScheduleInUpdateSeries}
        isOpen={isUpdateSeriesPublishModalOpen}
        onClose={onUpdateSeriesPublishModalClose}
      />

      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('scheduling:shiftDetails')}</DrawerHeader>
        <DrawerBody pb="100px">
          {isLoading ? (
            <LogoLoader />
          ) : (
            <>
              <Box
                sx={{
                  '& > *': { px: '4', py: '3', gridTemplateColumns: '140px 1fr' },
                  '& > *:nth-of-type(odd)': { background: 'primary.50' },
                }}
              >
                <SimpleGrid spacing="2">
                  <Text as="strong" flex="0 0 100px">
                    {t('common:title')}
                  </Text>
                  <Text>{data?.payload?.title}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:job')}</Text>
                  <Text>{data?.payload?.job_name}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:description')}</Text>
                  <Text>{data?.payload?.description || '-'}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:address')}</Text>
                  <Text>{formatAddress(data?.payload?.address) || '-'}</Text>
                </SimpleGrid>

                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:startDate')}</Text>
                  {data?.payload?.timezone_dateinfo ? (
                    <Text>
                      {data?.payload?.timezone_dateinfo?.start_date
                        ? formatYearMonthDay(data?.payload?.timezone_dateinfo?.start_date)
                        : '- '}
                      <br />
                      <Tag
                        mt="1"
                        bg="secondary.100"
                        size="sm"
                        color="secondary.600"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {data?.payload?.timezone}
                      </Tag>
                    </Text>
                  ) : (
                    <Text>{data?.payload?.start_date ? formatYearMonthDay(data?.payload?.start_date) : '- '}</Text>
                  )}
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:startTime')}</Text>
                  {data?.payload?.timezone_dateinfo ? (
                    <Text>
                      {data?.payload?.timezone_dateinfo?.start_date
                        ? formatTime(data?.payload?.timezone_dateinfo?.start_date)
                        : '- '}
                    </Text>
                  ) : (
                    <Text>{data?.payload?.start_time ? formatTime(data?.payload?.start_date) : '- '}</Text>
                  )}
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:endDate')}</Text>
                  {data?.payload?.timezone_dateinfo ? (
                    <Text>
                      {data?.payload?.timezone_dateinfo?.end_date
                        ? formatYearMonthDay(data?.payload?.timezone_dateinfo?.end_date)
                        : '- '}
                      <br />
                      <Tag
                        mt="1"
                        bg="secondary.100"
                        size="sm"
                        color="secondary.600"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {data?.payload?.timezone}
                      </Tag>
                    </Text>
                  ) : (
                    <Text>{data?.payload?.end_date ? formatYearMonthDay(data?.payload?.end_date) : '- '}</Text>
                  )}
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:endTime')}</Text>

                  {data?.payload?.timezone_dateinfo ? (
                    <Text>
                      {data?.payload?.timezone_dateinfo?.end_date
                        ? formatTime(data?.payload?.timezone_dateinfo?.end_date)
                        : '- '}
                    </Text>
                  ) : (
                    <Text>{data?.payload?.end_time ? formatTime(data?.payload?.end_date) : '- '}</Text>
                  )}
                </SimpleGrid>

                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:allDay')}</Text>
                  <Text>{data?.payload?.all_day ? 'Yes' : 'No'}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:repeat')}</Text>
                  <Text>{data?.payload?.repeat ? 'Yes' : 'No'}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:repeatType')}</Text>
                  <Text>{data?.payload?.rec_type ? data?.payload?.rec_type : '- '}</Text>
                </SimpleGrid>
                {data?.payload?.repeat_start_date ? (
                  <SimpleGrid spacing="2">
                    <Text as="strong">{t('scheduling:repeatStartDate')}</Text>
                    <Text>
                      {data?.payload?.repeat_start_date
                        ? format(
                            new Date(data?.payload?.repeat_start_date),
                            companyInfo?.companyDateFormat || MONTH_DAY_YEAR_FORMAT
                          )
                        : '- '}
                    </Text>
                  </SimpleGrid>
                ) : null}
                {data?.payload?.repeat_date ? (
                  <SimpleGrid spacing="2">
                    <Text as="strong">{t('scheduling:repeatEndDate')}</Text>
                    <Text>
                      {data?.payload?.repeat_date
                        ? format(
                            new Date(data?.payload?.repeat_date),
                            companyInfo?.companyDateFormat || MONTH_DAY_YEAR_FORMAT
                          )
                        : '- '}
                    </Text>
                  </SimpleGrid>
                ) : null}
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:published')}</Text>
                  <Text>{data?.payload?.is_publish ? t('common:yes') : t('common:no')}</Text>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:color')}</Text>
                  <Box>
                    {data?.payload?.color_hex_code ? (
                      <Box background={data?.payload?.color_hex_code} height="20px" width="40px" borderRadius="base" />
                    ) : (
                      '- '
                    )}
                  </Box>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:assignedBy')}</Text>
                  <Flex alignItems="center">
                    <Image
                      fallback={<FallbackAvatar name={data?.payload?.assigned_by_user} boxSize="32px" />}
                      boxSize="32px"
                      objectFit="cover"
                      borderRadius="base"
                      src={data?.payload?.assigned_by_user_image}
                      alt={data?.payload?.assigned_by_user}
                    />
                    <Text ml="2">{data?.payload?.assign_by_user}</Text>
                  </Flex>
                </SimpleGrid>
                <SimpleGrid spacing="2">
                  <Text as="strong">{t('common:assignedTo')}</Text>
                  <div>
                    {Array.isArray(data?.payload?.assignee) &&
                      data?.payload?.assignee.map((item: any) => {
                        return (
                          <Flex alignItems="center" _notLast={{ mb: '2' }} key={item.userId}>
                            <Image
                              fallback={<FallbackAvatar name={item.firstName + item.lastName} boxSize="32px" />}
                              boxSize="32px"
                              objectFit="cover"
                              borderRadius="base"
                              src={item?.employeeImage}
                              alt={item?.firstName}
                            />
                            <Text ml="2">{item?.firstName + ' ' + item?.lastName}</Text>
                          </Flex>
                        );
                      })}
                  </div>
                </SimpleGrid>
              </Box>
              {userStore?.userInfo?.roleId !== roles.EMPLOYEE || userStore?.permissions?.manageSchedules ? (
                <DrawerFixedButton>
                  <SimpleGrid columns={data?.payload?.is_publish ? 2 : 3} spacing={2}>
                    {!data?.payload?.is_publish ? (
                      <Button
                        type="submit"
                        bg="primary.50"
                        color="primary.400"
                        _hover={{
                          bg: 'primary.100',
                        }}
                        _active={{
                          bg: 'primary.100',
                        }}
                        width="100%"
                        size="lg"
                        isLoading={!data?.payload?.repeat && publishScheduleStatus === 'loading'}
                        onClick={
                          data?.payload?.repeat ? handlePublishButtonClickforUpdateSeries : handlePublishButtonClick
                        }
                      >
                        {t('common:publish')}
                      </Button>
                    ) : null}
                    <Button
                      type="submit"
                      bg="secondary.50"
                      color="secondary.400"
                      _hover={{
                        bg: 'secondary.100',
                      }}
                      _active={{
                        bg: 'secondary.100',
                      }}
                      width="100%"
                      size="lg"
                      onClick={data?.payload?.repeat ? handleEditButtonClickForUpdateSeries : handleEditButtonClick}
                    >
                      {t('common:update')}
                    </Button>

                    <Button
                      type="submit"
                      bg="rgba(250, 85, 85, 0.1)"
                      color="secondary.700"
                      fontWeight="semibold"
                      _hover={{
                        bg: 'rgba(250, 85, 85, 0.2)',
                      }}
                      _active={{
                        bg: 'rgba(250, 85, 85, 0.2)',
                      }}
                      width="100%"
                      size="lg"
                      onClick={data?.payload?.repeat ? handleDeleteButtonClickforUpdateSeries : handleDeleteButtonClick}
                    >
                      {t('common:delete')}
                    </Button>
                  </SimpleGrid>
                </DrawerFixedButton>
              ) : null}
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </>
  );
};

export default memo(ScheduleDetail);
